import React from "react";
import {
  Box,
  Flex,
  Text,
  Divider,
  Stack,
  Icon,
} from "@chakra-ui/react";

import {
  Phone,
  MessageCircle,
  Mail,
  User,
  Calendar,
  Clock,
  CheckCircle,
  AlertCircle,
} from "lucide-react";

import ReusableDrawer from "components/Drawers/ReusableDrawer";
import ReusableAvatar from "components/Avatar/ReusableAvatar";
import StatusBadge, {
  getStatusVariant,
} from "../../../../components/Badges/StatusBadge";
import { formatDate } from "utils/formatDate";


const getMethodIcon = (method) => {
  switch (method) {
    case "CALL":
    case "Call":
      return Phone;

    case "WHATSAPP":
    case "WhatsApp":
      return MessageCircle;

    case "EMAIL":
    case "Email":
      return Mail;

    default:
      return User;
  }
};


const getMethodLabel = (method) => {
  switch (method) {
    case "CALL":
    case "Call":
      return "Call";

    case "WHATSAPP":
    case "WhatsApp":
      return "WhatsApp";

    case "EMAIL":
    case "Email":
      return "Email";

    case "IN_PERSON":
    case "In Person":
      return "In Person";

    default:
      return method || "Unknown";
  }
};


const getStatusLabel = (status) => {
  switch (status) {
    case "CONTACTED":
      return "Contacted";

    case "NOT_REACHED":
      return "Not Reached";

    case "PENDING":
      return "Pending";

    case "Contacted":
      return "Contacted";

    case "Not Reached":
      return "Not Reached";

    case "Pending":
      return "Pending";

    default:
      return status || "Unknown";
  }
};


const ViewContactLogDrawer = ({
  isOpen,
  onClose,
  log,
}) => {

  if (!log) {
    return null;
  }

  const MethodIcon =
    getMethodIcon(
      log.contact_method
    );

  return (
    <ReusableDrawer
      isOpen={isOpen}
      onClose={onClose}
      headerContent={
        <Text fontSize="lg">
          Contact Log Details
        </Text>
      }

      bodyContent={
        <Flex
          direction="column"
          p={4}
        >

          {/* HEADER */}

          <Flex
            alignItems="center"
            justify="space-between"
            gap={3}
          >

            <Flex
              alignItems="center"
              gap={3}
              minW={0}
            >

              <ReusableAvatar
                name={
                  log.user_name ||
                  "User"
                }
              />

              <Box minW={0}>

                <Text
                  fontSize="xl"
                  fontWeight="bold"
                  wordBreak="break-word"
                >
                  {
                    log.user_name ||
                    "User"
                  }
                </Text>

                <Flex
                  alignItems="center"
                  gap={1}
                  color="gray.500"
                >

                  <Icon
                    as={MethodIcon}
                    boxSize={15}
                  />

                  <Text fontSize="sm">
                    Contacted via{" "}
                    {
                      getMethodLabel(
                        log.contact_method
                      )
                    }
                  </Text>

                </Flex>

              </Box>

            </Flex>


            {/* STATUS */}

            <StatusBadge
              label={
                getStatusLabel(
                  log.status
                )
              }
              variant={
                getStatusVariant(
                  log.status
                )
              }
            />

          </Flex>


          {/* DATE INFORMATION */}

          <Flex
            mt={4}
            alignItems={{
              base: "flex-start",
              md: "center",
            }}
            direction={{
              base: "column",
              md: "row",
            }}
            gap={3}
          >

            <Flex alignItems="center">

              <Icon
                as={Calendar}
                mr={1}
                color="gray.500"
              />

              <Text
                fontSize="sm"
                color="gray.500"
              >
                {formatDate(
                  log.contacted_at
                )}
              </Text>

            </Flex>


            <Flex alignItems="center">

              <Icon
                as={Clock}
                mr={1}
                color="gray.500"
              />

              <Text
                fontSize="sm"
                color="gray.500"
              >
                Follow-up:{" "}
                {
                  log.follow_up_date ||
                  "N/A"
                }
              </Text>

            </Flex>

          </Flex>


          <Divider my={5} />


          {/* CONTACT DETAILS */}

          <Stack spacing={4}>

            {/* STATUS */}

            <Box>

              <Text
                fontSize="xs"
                color="gray.400"
                mb={1}
              >
                Status
              </Text>

              <StatusBadge
                label={
                  getStatusLabel(
                    log.status
                  )
                }
                variant={
                  getStatusVariant(
                    log.status
                  )
                }
              />

            </Box>


            {/* REASON */}

            <Box>

              <Text
                fontSize="xs"
                color="gray.400"
              >
                Reason
              </Text>

              <Text fontWeight="500">
                {
                  log.reason ||
                  "No reason provided"
                }
              </Text>

            </Box>


            {/* NOTES */}

            <Box>

              <Text
                fontSize="xs"
                color="gray.400"
              >
                Notes
              </Text>

              <Text>
                {
                  log.notes ||
                  "No notes provided"
                }
              </Text>

            </Box>


            {/* OUTCOME */}

            <Box>

              <Text
                fontSize="xs"
                color="gray.400"
              >
                Outcome
              </Text>

              <Flex
                alignItems="center"
                gap={2}
              >

                <Icon
                  as={CheckCircle}
                  color="green.500"
                />

                <Text>
                  {
                    log.outcome ||
                    "Pending"
                  }
                </Text>

              </Flex>

            </Box>


            {/* NEXT ACTION */}

            <Box>

              <Text
                fontSize="xs"
                color="gray.400"
              >
                Next Action
              </Text>

              <Flex
                alignItems="center"
                gap={2}
              >

                <Icon
                  as={AlertCircle}
                  color="orange.500"
                />

                <Text>
                  {
                    log.next_action ||
                    "No action set"
                  }
                </Text>

              </Flex>

            </Box>

          </Stack>


          <Divider my={5} />


          {/* FOLLOW-UP */}

          <Box>

            <Text
              fontSize="xs"
              color="gray.400"
              mb={1}
            >
              Follow-up Reminder
            </Text>

            {log.follow_up_date ? (

              <Flex
                alignItems="center"
                gap={2}
              >

                <Icon
                  as={Clock}
                  color="orange.500"
                />

                <Text fontSize="sm">
                  Follow-up scheduled for{" "}
                  <strong>
                    {
                      log.follow_up_date
                    }
                  </strong>
                </Text>

              </Flex>

            ) : (

              <Text
                fontSize="sm"
                color="gray.500"
              >
                No follow-up scheduled
              </Text>

            )}

          </Box>


          <Divider my={5} />


          {/* LOGGED BY */}

          <Box>

            <Text
              fontSize="xs"
              color="gray.400"
            >
              Logged By
            </Text>

            <Text fontWeight="500">
              {
                log.admin_name ||
                "Admin"
              }
            </Text>

          </Box>

        </Flex>
      }
    />
  );
};

export default ViewContactLogDrawer;